import React from "react";
import { RECEIPT_DEFAULTS, RECEIPT_TOGGLES } from "@/lib/printTemplates";
import { ReceiptDoc, SAMPLE_RECEIPT } from "@/components/print/ReceiptDoc";
import { printReceiptData } from "@/lib/printing";
import { ImageField } from "@/components/ImageField";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Slider } from "@/components/ui/slider";
import { Printer, Plus, Trash2 } from "lucide-react";
import { useConfigEditor, EditorBar, Field, Toggle, Panel } from "./common";

const LABEL_NAMES = {
  table: "Маса", waiter: "Сервитьор", date: "Дата", receipt: "Номер на бележка", subtotal: "Сума преди отстъпка", discount: "Отстъпка",
  total: "Общо", vat: "ДДС", payment: "Плащане", cash: "В брой", card: "С карта", prebill: "Предварителна сметка", copy: "Копие", nonfiscal: "Нефискален надпис",
};

const Range = ({ label, value, min, max, step = 1, onChange, unit = "", testid }) => (
  <div>
    <div className="flex justify-between text-xs"><Label className="text-xs">{label}</Label><span>{value}{unit}</span></div>
    <Slider min={min} max={max} step={step} value={[Number(value)]} onValueChange={([v]) => onChange(v)} className="mt-2" data-testid={testid} />
  </div>
);

export default function ReceiptTab() {
  const ed = useConfigEditor("print_receipt", RECEIPT_DEFAULTS);
  const t = ed.value;
  if (!t) return null;
  const set = (patch) => ed.set((s) => ({ ...s, ...patch }));
  const setIn = (group, k, v) => ed.set((s) => ({ ...s, [group]: { ...s[group], [k]: v } }));
  const live = set;

  return (
    <div data-testid="receipt-tab">
      <EditorBar ed={ed} testid="receipt">
        <Button variant="outline" onClick={() => printReceiptData(t, SAMPLE_RECEIPT)} className="gap-1" data-testid="receipt-test-print"><Printer className="w-4 h-4" /> Тестов печат</Button>
      </EditorBar>
      <div className="grid xl:grid-cols-[1fr_360px] gap-5">
        <div className="grid md:grid-cols-2 gap-4 content-start">
          <Panel title="Принтер и хартия">
            <Field label="Име на принтера (за справка)" value={t.printer_name} onChange={(v) => set({ printer_name: v })} placeholder="напр. XPrinter XP-80C" testid="receipt-printer-name" />
            <div className="grid grid-cols-2 gap-2">
              <Field label="Ширина на ролката (мм)" type="number" value={t.paper_mm} onChange={(v) => set({ paper_mm: v })} testid="receipt-paper-mm" />
              <Field label="Печатна ширина (мм)" type="number" value={t.printable_mm} onChange={(v) => set({ printable_mm: v })} testid="receipt-printable-mm" />
            </div>
            <Range label="Полета отгоре/отдолу" value={t.margin_mm} min={0} max={15} unit=" мм" onChange={(v) => live({ margin_mm: v })} testid="receipt-margin" />
            <Range label="Размер на шрифта" value={t.font_size} min={9} max={18} unit=" px" onChange={(v) => live({ font_size: v })} testid="receipt-font-size" />
            <Range label="Разстояние между редовете" value={t.line_height} min={1} max={2} step={0.05} onChange={(v) => live({ line_height: v })} testid="receipt-line-height" />
          </Panel>
          <Panel title="Лого и заглавие">
            <Toggle label="Показвай лого" checked={t.logo_enabled} onChange={(v) => set({ logo_enabled: v })} testid="receipt-logo-enabled" />
            <ImageField label="Лого" value={t.logo_url} onChange={(v) => set({ logo_url: v, logo_enabled: !!v || t.logo_enabled })} testid="receipt-logo" />
            <Range label="Ширина на логото" value={t.logo_mm} min={10} max={72} unit=" мм" onChange={(v) => live({ logo_mm: v })} testid="receipt-logo-size" />
            <Field label="Заглавие" value={t.title} onChange={(v) => set({ title: v })} testid="receipt-title" />
            <div><Label className="text-xs">Подзаглавие (адрес, телефон…)</Label><Textarea value={t.subtitle} onChange={(e) => set({ subtitle: e.target.value })} data-testid="receipt-subtitle" /></div>
          </Panel>
          <Panel title="Елементи на бележката">
            {RECEIPT_TOGGLES.map(([k, l]) => <Toggle key={k} label={l} checked={t.show[k]} onChange={(v) => setIn("show", k, v)} testid={`receipt-show-${k}`} />)}
          </Panel>
          <Panel title="Текстове">
            <div><Label className="text-xs">Текст в края</Label><Textarea value={t.footer} onChange={(e) => set({ footer: e.target.value })} data-testid="receipt-footer" /></div>
            <Label className="text-xs">Допълнителни редове</Label>
            {(t.extra_lines || []).map((line, i) => (
              <div key={i} className="flex gap-1">
                <Field label="" value={line} onChange={(v) => set({ extra_lines: t.extra_lines.map((x, j) => (j === i ? v : x)) })} testid={`receipt-extra-${i}`} />
                <Button variant="ghost" size="icon" onClick={() => set({ extra_lines: t.extra_lines.filter((_, j) => j !== i) })}><Trash2 className="w-4 h-4" /></Button>
              </div>
            ))}
            <Button variant="outline" size="sm" onClick={() => set({ extra_lines: [...(t.extra_lines || []), ""] })} className="gap-1" data-testid="receipt-add-line"><Plus className="w-4 h-4" /> Добави ред</Button>
          </Panel>
          <Panel title="Надписи">
            <div className="grid grid-cols-2 gap-2">
              {Object.keys(LABEL_NAMES).map((k) => <Field key={k} label={LABEL_NAMES[k]} value={t.labels[k]} onChange={(v) => setIn("labels", k, v)} testid={`receipt-label-${k}`} />)}
            </div>
          </Panel>
        </div>
        <div className="xl:sticky xl:top-4 h-fit">
          <div className="text-xs text-muted-foreground mb-2">Преглед ({t.paper_mm} мм)</div>
          <div className="bg-neutral-200 rounded-2xl p-4 flex justify-center overflow-auto">
            <div className="bg-white shadow-lg" style={{ width: `${t.paper_mm}mm` }} data-testid="receipt-preview"><ReceiptDoc tpl={t} data={SAMPLE_RECEIPT} /></div>
          </div>
        </div>
      </div>
    </div>
  );
}
